<template>
  <div>
    <data-table
      ref="dataTableRef"
      :config="dataTableConfig"
      :endpoint="endpoint"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useFiltro } from '../../composables/useFiltro';

interface DataTableInstance {
  loadData: () => void;
}

// Get i18n composable
const { t, n } = useI18n();
const { initialized } = useFiltro();

// Reactive data
const dataTableRef = ref<DataTableInstance | null>(null);
const dataLoaded = ref<boolean>(false);

// Computed properties
const endpoint = computed(() => '/index.php/relatorios/comparativo_servicos/lista_comparativo');

/**
 * Somas do conjunto CARREGADO, base dos percentuais de representatividade.
 * Usar o carregado (e nao um total vindo do servidor) e deliberado: assim os
 * percentuais fecham com o rodape que a pessoa esta vendo, inclusive apos
 * filtrar.
 */
const totais = ref<{ qtd: number; valor: number }>({ qtd: 0, valor: 0 });

/** Fatia de `parte` sobre `total`, ou vazio quando nao ha base. */
const formataFatia = (parte: number, total: number): string =>
  total > 0 ? n(parte / total, 'percent') : '';

const tableColumns = computed(() => [
  {
    data: 'servico_nome',
    title: t('Serviço'),
    width: 200,
    sortable: true
  },
  {
    data: 'servicoscategoria_nome',
    title: t('Categoria'),
    width: 150,
    sortable: true
  },
  {
    data: 'qtd_orcamento',
    title: t('Qtd Orçamento'),
    width: 120,
    sortable: true,
    footerType: 'sum'
  },
  {
    data: 'total_orcamento',
    title: t('Total Orçamento'),
    width: 150,
    sortable: true,
    renderType: 'currency',
    footerType: 'sum'
  },
  {
    data: 'media_orcamento',
    title: t('Média Orçamento'),
    width: 150,
    sortable: true,
    renderType: 'currency',
    footerType: 'avg'
  },
  {
    data: 'qtd_compromisso',
    title: t('Qtd Compromisso'),
    width: 120,
    sortable: true,
    footerType: 'sum'
  },
  {
    data: 'total_compromisso',
    title: t('Total Compromisso'),
    width: 150,
    sortable: true,
    renderType: 'currency',
    footerType: 'sum'
  },
  {
    data: 'media_compromisso',
    title: t('Média Compromisso'),
    width: 150,
    sortable: true,
    renderType: 'currency',
    footerType: 'avg'
  },
  {
    // Representatividade: quanto este servico pesa no total carregado. A base e
    // a MESMA soma que o rodape mostra, entao os percentuais fecham com o que
    // esta na tela — inclusive depois de filtrar (chamado 2026-0017).
    data: 'servico_nome',
    title: t('% Qtd'),
    width: 100,
    sortable: false,
    cellRenderer: ({ data }: { data: Record<string, unknown> }) =>
      formataFatia(Number(data?.qtd_orcamento) || 0, totais.value.qtd)
  },
  {
    data: 'servico_nome',
    title: t('% Valor'),
    width: 100,
    sortable: false,
    cellRenderer: ({ data }: { data: Record<string, unknown> }) =>
      formataFatia(Number(data?.total_orcamento) || 0, totais.value.valor)
  }
]);

const dataTableConfig = computed(() => {
  return {
    tableId: 'tabelaComparativoServicos',
    filename: `comparativo_servicos_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 100,
    pagination: true,
    footerLabel: t('Totais'),
    // As linhas chegavam na ordem em que o banco devolveu — nem alfabetica, nem
    // por quantidade, nem por valor (chamado 2026-0017). Num comparativo o que
    // interessa primeiro e o que pesa mais, entao o padrao passa a ser o maior
    // total orcado no topo. A pessoa continua podendo ordenar por qualquer
    // coluna; isto so define por onde a tela ABRE.
    defaultSortField: 'total_orcamento',
    defaultSortOrder: 'desc',
    rowSelection: 'single',
    resizableColumns: true,
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const filterConfig = computed(() => {
  return {
    searchName: t('Procurar'),
    choosePlaceholder: t('Escolha'),
    refreshName: t('Atualizar'),
    printName: t('Imprimir'),
    debug: false,
    columns: {
      'servico_nome': 'select',
      'servicoscategoria_nome': 'text',
      'qtd_orcamento': 'range',
      'total_orcamento': 'range',
      'media_orcamento': 'range',
      'qtd_compromisso': 'range',
      'total_compromisso': 'range',
      'media_compromisso': 'range'
    }
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('CSV'),
    loading: false
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

// Methods
const loadData = () => {
  const stack = new Error().stack;
  log('ComparativoServicosTable', 'loadData', 'Triggering table refresh', {
    callStack: stack?.split('\n').slice(1, 4).join('\n')
  });
  if (dataTableRef.value) {
    dataTableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('ComparativoServicosTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');

  // Base dos percentuais de representatividade, recalculada a cada carga para
  // acompanhar o filtro vigente.
  // O tipo do acumulador é explícito: sem ele o TypeScript infere `unknown`
  // para `acc.qtd`/`acc.valor` a partir do `Record<string, unknown>` da linha,
  // e o vue-tsc recusa a soma (TS18046).
  totais.value = (data as Record<string, unknown>[]).reduce<{ qtd: number; valor: number }>(
    (acc, linha) => ({
      qtd: acc.qtd + (Number(linha?.qtd_orcamento) || 0),
      valor: acc.valor + (Number(linha?.total_orcamento) || 0)
    }),
    { qtd: 0, valor: 0 }
  );

  dataLoaded.value = true;
  eventBus.emit('globalMessage.success', t(`Carregados`) + ` ${data.length} ` + t(`registros do comparativo de serviços`));
};

const handleDataError = (error: { message?: string } | Error): void => {
  log('ComparativoServicosTable', 'handleDataError', 'Error loading data', error);
  const message = error instanceof Error ? error.message : error.message || '';
  eventBus.emit('globalMessage.error', t('Erro ao carregar comparativo de serviços: ') + message);
};

// Named handler for proper cleanup
const onFilterUpdated = (): void => {
  log('ComparativoServicosTable', 'event:filter.updated', 'Received, reloading data');
  loadData();
};

// Lifecycle
onMounted(() => {
  log('ComparativoServicosTable', 'mounted', 'Setting up event listeners', {
    filterInitialized: initialized.value
  });

  // Don't load data on mount - let DataTable handle initial load via autoLoad
  log('ComparativoServicosTable', 'mounted', 'Initial data load will be handled by DataTable autoLoad');

  eventBus.on('filter.updated', onFilterUpdated);
});

onBeforeUnmount(() => {
  log('ComparativoServicosTable', 'beforeUnmount', 'Cleaning up event listeners');
  eventBus.off('filter.updated', onFilterUpdated);
});

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
</style>
